import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtDateTime, TABLE_HEAD_ROW_SX } from '../../Utils/format';
import { formatEventMessage, getSeverityConfig, getCanonicalSeverity } from '../../Utils/eventHelpers';
import ModalTemplate from '../Modal/ModalTemplate';
import useCustomAuth from '../../Context/Hooks/useCustomAuth';
import ClientBadge from './ClientBadge';
import LoadingSkeleton from './LoadingSkeleton';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Checkbox,
  Chip,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Avatar,
  Paper,
  Tooltip,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import SearchIcon from '@mui/icons-material/Search';

const SELECT_SX = {
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
};

const SeverityBadge = ({ severity, onLevelClick }) => {
  const { t } = useTranslation('events');
  const config = getSeverityConfig(severity, t);
  const canonicalLevel = getCanonicalSeverity(severity);

  const badgeContent = (
    <Box
      onClick={onLevelClick ? (e) => { e.stopPropagation(); onLevelClick(canonicalLevel); } : undefined}
      role={onLevelClick ? 'button' : undefined}
      tabIndex={onLevelClick ? 0 : undefined}
      onKeyDown={onLevelClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLevelClick(canonicalLevel); } } : undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        pl: '7px',
        pr: '9px',
        py: '3px',
        borderRadius: '999px',
        bgcolor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
        cursor: onLevelClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.15s ease-in-out',
        '&:hover': onLevelClick ? {
          filter: 'brightness(0.92)',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
        } : {},
      }}
    >
      <config.Icon sx={{ fontSize: 12 }} />
      {config.label}
    </Box>
  );

  return onLevelClick ? (
    <Tooltip title={t('events:filters.filterByLevel', { level: config.label, defaultValue: `Filtrar por ${config.label}` })} arrow>
      {badgeContent}
    </Tooltip>
  ) : badgeContent;
};

SeverityBadge.propTypes = {
  severity: PropTypes.string,
  onLevelClick: PropTypes.func,
};

const StatusBadge = ({ resolved }) => {
  const { t } = useTranslation('events');

  if (resolved) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          pl: '8px',
          pr: '10px',
          py: '3px',
          borderRadius: '999px',
          bgcolor: '#f9fafb',
          color: '#9ca3af',
          border: '1px solid #e5e7eb',
          fontSize: 12.5,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          fill="none"
          sx={{
            width: 13,
            height: 13,
            stroke: '#9ca3af',
            strokeWidth: 2.5,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            flexShrink: 0,
          }}
        >
          <polyline points="20 6 9 17 4 12" />
        </Box>

        {t('status.resolved')}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        pl: '8px',
        pr: '10px',
        py: '3px',
        borderRadius: '999px',
        bgcolor: 'rgba(245,158,11,0.08)',
        color: '#d98c1f',
        border: '1px solid rgba(245,158,11,0.25)',
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: '#f59e0b',
          flexShrink: 0,
        }}
      />

      {t('status.open')}
    </Box>
  );
};

StatusBadge.propTypes = {
  resolved: PropTypes.bool,
};

const parseComments = (commentVal) => {
  if (!commentVal) return [];
  if (Array.isArray(commentVal)) return commentVal;
  try {
    const parsed = JSON.parse(commentVal);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (e) {
    // If it's a raw string, convert it to a legacy comment
    return [{
      author: 'Sistema',
      text: commentVal,
      createdAt: new Date().toISOString()
    }];
  }
  return [{
    author: 'Sistema',
    text: commentVal,
    createdAt: new Date().toISOString()
  }];
};

const CommentsCell = ({ eventId, comment, onSave }) => {
  const { t } = useTranslation(['events', 'common']);
  const { user: authUser } = useCustomAuth();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const commentList = useMemo(() => parseComments(comment), [comment]);

  const handleSave = () => {
    if (!draft.trim()) return;
    onSave?.(eventId, draft.trim());
    setDraft('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          cursor: 'pointer',
          color: commentList.length > 0 ? '#1B75BB' : '#d1d5db',
          fontSize: 12.5,
          userSelect: 'none',
          '&:hover': {
            color: '#1B75BB',
          },
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          fill="none"
          sx={{
            width: 14,
            height: 14,
            stroke: 'currentColor',
            strokeWidth: 2,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            flexShrink: 0,
          }}
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </Box>

        {commentList.length > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              bgcolor: '#e8f1fa',
              color: '#1B75BB',
              px: '5px',
              py: '1px',
              borderRadius: '10px',
              border: '1px solid #c9ddf2',
            }}
          >
            {commentList.length}
          </Typography>
        )}
      </Box>

      <ModalTemplate
        open={open}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon sx={{ color: '#1B75BB', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 600 }}>
              {t('events:comments.historyTitle', 'Histórico de Comentários')} (ID: {eventId})
            </Typography>
          </Box>
        }
        size="sm"
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '380px', pt: 1 }}>
            {/* Scrollable list of comments */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                pr: 1,
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {commentList.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary',
                    gap: 1.5,
                  }}
                >
                  <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.6 }} />
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 280 }}>
                    Sem notas ou comentários registados para este evento. Adicione uma nota abaixo.
                  </Typography>
                </Box>
              ) : (
                commentList.map((c, index) => {
                  const isSelf = Boolean(
                    authUser && (
                      c.author === authUser.email ||
                      c.author === authUser.username ||
                      c.author === authUser.name ||
                      c.author === 'Operador'
                    )
                  );
                  return (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 1.75,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: isSelf ? '#c9ddf2' : 'divider',
                        bgcolor: isSelf ? '#f9fbfd' : 'background.paper',
                      }}
                    >
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: 11,
                            bgcolor: isSelf ? '#1B75BB' : 'grey.400',
                            color: '#fff',
                          }}
                        >
                          {c.author ? c.author.charAt(0).toUpperCase() : 'U'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontSize: 12.5, fontWeight: 600, display: 'inline', color: isSelf ? '#1B75BB' : 'text.primary' }}>
                            {c.author}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: 11.5, color: 'text.secondary', display: 'inline', ml: 0.5 }}>
                            {t('events:comments.addedComment', 'adicionou um comentário')}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                          {fmtDateTime(c.createdAt)}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1, borderColor: isSelf ? 'rgba(27,117,187,0.08)' : 'rgba(0,0,0,0.04)' }} />

                      {/* Text Body */}
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: 12.5,
                          color: 'text.primary',
                          lineHeight: 1.45,
                        }}
                      >
                        {c.text}
                      </Typography>
                    </Paper>
                  );
                })
              )}
            </Box>

            {/* Input area */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                size="small"
                placeholder={t('events:comments.placeholder', 'Adicionar nota ao histórico do evento...')}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 13,
                    bgcolor: '#fafafa',
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!draft.trim()}
                sx={{
                  borderRadius: '8px',
                  minWidth: '40px',
                  width: '40px',
                  height: '40px',
                  p: 0,
                  boxShadow: 'none',
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};

CommentsCell.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  comment: PropTypes.string,
  onSave: PropTypes.func,
};

const formatDateTime = (createdAt, fallback) => {
  if (!createdAt) {
    return fallback ?? '—';
  }
  return fmtDateTime(createdAt);
};

const EventsTable = ({
  events = [],
  totalCount = 0,
  page = 0,
  rowsPerPage = 20,
  search = '',
  severityLevel = '',
  statusFilter = '',
  loading = false,
  onPageChange,
  onRowsPerPageChange,
  onSearchChange,
  onSeverityLevelChange,
  onStatusFilterChange,
  onSaveComment,
  onResolve,
  onMarkAllRead,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const { user: authUser } = useCustomAuth();
  const currentUserRole = authUser?.role ?? 'USER';

  const levelOptions = [
    { value: 'CRITICAL', label: t('events:filters.critical') },
    { value: 'WARNING', label: t('events:filters.warning') },
    { value: 'INFO', label: t('events:filters.info') },
  ];

  const statusOptions = [
    { value: 'open', label: t('events:status.open') },
    { value: 'resolved', label: t('events:status.resolved') },
  ];

  const hasActiveFilters = Boolean(search)
    || (Array.isArray(severityLevel) && severityLevel.length > 0)
    || (Array.isArray(statusFilter)
        ? !(statusFilter.length === 1 && statusFilter[0] === 'open')
        : Boolean(statusFilter && statusFilter !== 'open'));

  const handleClearFilters = () => {
    onSearchChange?.('');
    onSeverityLevelChange?.([]);
    onStatusFilterChange?.(['open']);
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder={t('events:filters.search')}
            value={search}
            onChange={(event) => onSearchChange?.(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: 16,
                      color: 'text.disabled',
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 220,
              '& .MuiInputBase-root': {
                fontSize: 13,
              },
              '& .MuiOutlinedInput-root fieldset': {
                borderColor: 'divider',
              },
            }}
          />

          <Select
            multiple
            size="small"
            value={severityLevel || []}
            onChange={(event) => {
              const val = event.target.value;
              onSeverityLevelChange?.(typeof val === 'string' ? val.split(',') : val);
            }}
            displayEmpty
            renderValue={(selected) => (
              !selected || selected.length === 0
                ? <span style={{ opacity: 0.55 }}>{t('events:filters.allLevels')}</span>
                : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => {
                      const label = levelOptions.find(opt => opt.value === val)?.label || val;
                      return <Chip key={val} label={label} size="small" />;
                    })}
                  </Box>
            )}
            sx={{
              ...SELECT_SX,
              minWidth: 160,
              maxWidth: 260,
            }}
          >
            {levelOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: 13 }}
              >
                <Checkbox size="small" checked={(severityLevel || []).includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>

          <Select
            multiple
            size="small"
            value={statusFilter || []}
            onChange={(event) => {
              const val = event.target.value;
              onStatusFilterChange?.(typeof val === 'string' ? val.split(',') : val);
            }}
            displayEmpty
            renderValue={(selected) => (
              !selected || selected.length === 0
                ? <span style={{ opacity: 0.55 }}>{t('events:filters.allSources')}</span>
                : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => {
                      const label = statusOptions.find(opt => opt.value === val)?.label || val;
                      return <Chip key={val} label={label} size="small" />;
                    })}
                  </Box>
            )}
            sx={{
              ...SELECT_SX,
              minWidth: 160,
              maxWidth: 260,
            }}
          >
            {statusOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: 13 }}
              >
                <Checkbox size="small" checked={(statusFilter || []).includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>

          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearFilters}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('events:filters.clearFilters', t('common:tableActions.clearFilters', 'Limpar filtros'))}
            </Button>
          )}
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DoneIcon sx={{ fontSize: 14 }} />}
          onClick={onMarkAllRead}
          sx={{
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 500,
            borderColor: 'divider',
            color: 'text.secondary',
            flexShrink: 0,
          }}
        >
          {t('events:actions.markAllRead')}
        </Button>
      </Box>

      <LoadingSkeleton loading={loading} preset="table" height={320} sx={{ borderRadius: 0 }}>
        {events.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography
              variant="body2"
              color="text.disabled"
            >
              {t('events:table.empty')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& .MuiTableCell-root': {
                  fontSize: 13,
                  borderColor: 'divider',
                },
              }}
            >
            <TableHead>
              <TableRow sx={TABLE_HEAD_ROW_SX}>
                {[
                  t('events:table.level'),
                  t('events:table.event'),
                  t('events:table.device'),
                  t('events:table.storeClient'),
                  t('events:table.occurred'),
                  t('events:table.status'),
                  t('events:table.comments'),
                  '',
                ].map((header, index) => (
                  <TableCell
                    key={header || index}
                    sx={{
                      ...(index === 0 && { pl: 2.5 }),
                      ...(index === 7 && { pr: 1.5 }),
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {events.map((event, index) => {
                const eventKey = event.id ?? index;
                const resolved = event.isResolved || event.status === 'RESOLVED';
                const when = formatDateTime(event.createdAt, event.timeLabel);

                return (
                  <TableRow
                    key={eventKey}
                    sx={{
                      '&:last-child .MuiTableCell-root': {
                        borderBottom: 'none',
                      },
                      '&:hover': {
                        bgcolor: '#f9fafb',
                      },
                    }}
                  >
                    <TableCell sx={{ py: '14px', pl: '20px', pr: '16px' }}>
                      <SeverityBadge
                        severity={event.severity}
                        onLevelClick={onSeverityLevelChange ? (lvl) => onSeverityLevelChange([lvl]) : undefined}
                      />
                    </TableCell>

                    <TableCell sx={{ py: '14px', px: '16px' }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: '#1f2937',
                          lineHeight: 1.4,
                        }}
                      >
                        {formatEventMessage(event, t)}
                      </Typography>

                      {(event.eventCode || event.id) && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontFamily: 'monospace',
                            color: '#9ca3af',
                            mt: '2px',
                          }}
                        >
                          {event.eventCode
                            ? `evt-${event.eventCode}`
                            : event.id}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: '14px', px: '16px' }}>
                      {event.deviceId ? (
                        <Link
                          component={RouterLink}
                          to={`/Gateway/Devices/${event.deviceId}`}
                          underline="hover"
                          sx={{
                            fontSize: 12.5,
                            fontFamily: 'monospace',
                            fontWeight: 500,
                            color: 'primary.main',
                            display: 'block',
                            lineHeight: 1.3,
                          }}
                        >
                          {event.deviceName || event.deviceId}
                        </Link>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontFamily: 'monospace',
                            fontWeight: 500,
                            color: '#1f2937',
                          }}
                        >
                          {event.deviceName || event.deviceId || '—'}
                        </Typography>
                      )}

                      {event.zone && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: '#6b7280',
                            mt: '2px',
                          }}
                        >
                          {event.zone}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: '14px', px: '16px' }}>
                      {event.storeId && event.storeName && event.storeName !== '—' ? (
                        <Link
                          component={RouterLink}
                          to={`/Gateway/Stores/${event.storeId}`}
                          underline="hover"
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'primary.main',
                            display: 'block',
                            lineHeight: 1.3,
                          }}
                        >
                          {event.storeName}
                        </Link>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: event.storeName && event.storeName !== '—' ? '#1f2937' : 'text.secondary',
                            display: 'block',
                            lineHeight: 1.3,
                          }}
                        >
                          {event.storeName && event.storeName !== '—' ? event.storeName : '—'}
                        </Typography>
                      )}

                      {event.clientName && event.clientName !== '—' && (
                        (currentUserRole === 'GATEWAY_ADMIN' || currentUserRole === 'CLIENT_ADMIN') && event.clientId ? (
                          <Link
                            component={RouterLink}
                            to={`/Gateway/Clients/${event.clientId}`}
                            underline="none"
                            sx={{ display: 'inline-block', mt: 0.5 }}
                          >
                            <ClientBadge clientName={event.clientName} />
                          </Link>
                        ) : (
                          <Box sx={{ display: 'inline-block', mt: 0.5 }}>
                            <ClientBadge clientName={event.clientName} />
                          </Box>
                        )
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        py: '14px',
                        px: '16px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'monospace',
                          color: '#6b7280',
                        }}
                      >
                        {when}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: '14px', px: '16px' }}>
                      <StatusBadge resolved={resolved} />
                    </TableCell>

                    <TableCell sx={{ py: '14px', px: '16px' }}>
                      <CommentsCell
                        eventId={eventKey}
                        comment={event.comments}
                        onSave={onSaveComment}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        py: '14px',
                        pl: '16px',
                        pr: '20px',
                        textAlign: 'right',
                      }}
                    >
                      {!resolved && (
                        <Typography
                          component="span"
                          onClick={() => onResolve?.(eventKey, event)}
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#1f2937',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {t('events:actions.resolve')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      </LoadingSkeleton>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50]}
        showFirstButton
        showLastButton
        labelRowsPerPage={t('common:pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => (
          `${from}–${to} ${t('common:pagination.of')} ${count}`
        )}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          fontSize: 13,
        }}
      />
    </Box>
  );
};

EventsTable.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  totalCount: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  search: PropTypes.string,
  severityLevel: PropTypes.string,
  statusFilter: PropTypes.string,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onSeverityLevelChange: PropTypes.func,
  onStatusFilterChange: PropTypes.func,
  onSaveComment: PropTypes.func,
  onResolve: PropTypes.func,
  onMarkAllRead: PropTypes.func,
};

export default EventsTable;
